import { HT } from "../siteConfig";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { gsap } from "gsap";
// import { Volume2, VolumeX } from "lucide-react";

// Desktop images
import Slider1 from "../assets/hero-slider-4.jpeg"
import Slider2 from "../assets/hero-slider-2.jpg"
import Slider3 from "../assets/hero-slider-3.jpg"
import Slider4 from "../assets/hero-slider-1.jpeg"

// Mobile images — replace these with your actual mobile asset filenames
import Slider1Mobile from "../assets/phone-hero-slider-1.webp"
import Slider2Mobile from "../assets/phone-hero-slider-2.webp"
import Slider3Mobile from "../assets/phone-hero-slider-3.webp"
import Slider4Mobile from "../assets/phone-hero-slider-4.jpeg"

// // Video slide — swap to your actual filenames
// import HeroVideo from "../assets/hero-bg-video.mp4"
// import HeroVideoMobile from "../assets/hero-bg-video.mp4"
// // Optional poster frame (shown while the video loads / before it plays)
// import HeroVideoPoster from "../assets/hero-slider-4.jpeg"

const slides = [
  // {
  //   type: "video",
  //   video: HeroVideo,
  //   videoMobile: HeroVideoMobile,
  //   poster: HeroVideoPoster,
  //   eyebrow: "See our clinics in action",
  //   headline: "Where Confidence\nBegins to Grow",
  //   sub: "A closer look inside our state-of-the-art facilities and the care that goes into every procedure.",
  //   stat: { value: "97%", label: "Success Rate" },
  //   tags: ["Advanced", "Natural", "Trusted"],
  // },
  {
    type: "image",
    image: Slider1,
    imageMobile: Slider1Mobile,
    eyebrow: "Trusted by 10,000+ patients across India",
    headline: "Your Trusted Partner\nin Hair Restoration",
    sub: "Advanced FUE & DHI techniques with natural-looking, permanent results.",
    stat: { value: "97%", label: "Success Rate" },
    tags: ["Advanced", "Natural", "Trusted"],
  },
  {
    type: "image",
    image: Slider2,
    imageMobile: Slider2Mobile,
    eyebrow: "Expert surgeons. World-class facilities.",
    headline: "Leading the Way\nin Hair Transplant",
    sub: "State-of-the-art clinics with personalised care at every step of your journey.",
    stat: { value: "15k+", label: "Happy Patients" },
    tags: ["Personalised", "Expert", "Proven"],
  },
  {
    type: "image",
    image: Slider3,
    imageMobile: Slider3Mobile,
    eyebrow: "Mumbai · Pune · Bengaluru · Shirdi",
    headline: "Four Clinics, One\nStandard of Excellence",
    sub: "Serving patients nationwide with the same world-class standard of care.",
    stat: { value: "4.8★", label: "Patient Rating" },
    tags: ["Caring", "Reliable", "Nationwide"],
  },
  {
    type: "image",
    image: Slider4,
    imageMobile: Slider4Mobile,
    eyebrow: "Transform your confidence with our expert solutions.",
    headline: "Experience the Difference\nin Hair Restoration",
    sub: "Customised treatments designed to meet your unique needs and deliver exceptional results.",
    stat: { value: "100%", label: "Satisfaction Rate" },
    tags: ["Customised", "Exceptional", "Transformative"],
  }
];

// Tailwind's `md` breakpoint — switch bg image below this width
const MOBILE_BREAKPOINT = 768;

function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isMobile = useIsMobile();

  // ── Slide list actually shown on this device ──
  // On desktop the video slide has no real content (just a static poster),
  // so we skip it entirely there and only include it on mobile.
  const visibleSlides = useMemo(
    () => (isMobile ? slides : slides.filter((sl) => sl.type !== "video")),
    [isMobile]
  );

  // If the device type changes (resize across the breakpoint) and the
  // current index is now out of range, clamp it back to a valid slide.
  useEffect(() => {
    if (current > visibleSlides.length - 1) {
      setCurrent(0);
    }
  }, [visibleSlides, current]);

  // Refs for GSAP targets
  const eyebrowRef = useRef(null);
  const headlineRef = useRef(null);
  const subRef = useRef(null);
  const ctaRef = useRef(null);
  const statCardRef = useRef(null);
  const tagsCardRef = useRef(null);
  const scrollHintRef = useRef(null);
  const btnRef = useRef(null);
  // const [isMuted, setIsMuted] = useState(true);

  // // Refs to each <video> element in the BG slides, keyed by slide index
  // const videoRefs = useRef({});

  // ── Initial mount animation ──
  useEffect(() => {
    const ctx = gsap.context(() => {

      const leftTargets = [
        eyebrowRef.current,
        headlineRef.current,
        subRef.current,
        ctaRef.current,
      ].filter(Boolean);

      const rightTargets = [
        statCardRef.current,
        tagsCardRef.current,
      ].filter(Boolean);

      if (leftTargets.length === 0 && rightTargets.length === 0) return;

      gsap.set([...leftTargets, ...rightTargets], { opacity: 0, y: 22 });
      gsap.set(scrollHintRef.current, { opacity: 0 });

      const tl = gsap.timeline({ delay: 0.2 });

      if (eyebrowRef.current)  tl.to(eyebrowRef.current,  { opacity: 1, y: 0, duration: 0.55, ease: "power2.out" });
      if (headlineRef.current) tl.to(headlineRef.current, { opacity: 1, y: 0, duration: 0.6,  ease: "power2.out" }, "-=0.3");
      if (subRef.current)      tl.to(subRef.current,      { opacity: 1, y: 0, duration: 0.55, ease: "power2.out" }, "-=0.3");
      if (ctaRef.current)      tl.to(ctaRef.current,      { opacity: 1, y: 0, duration: 0.5,  ease: "power2.out" }, "-=0.25");
      if (statCardRef.current) tl.to(statCardRef.current, { opacity: 1, y: 0, duration: 0.55, ease: "power2.out" }, "-=0.4");
      if (tagsCardRef.current) tl.to(tagsCardRef.current, { opacity: 1, y: 0, duration: 0.5,  ease: "power2.out" }, "-=0.35");
      if (scrollHintRef.current) tl.to(scrollHintRef.current, { opacity: 0.25, duration: 0.6, ease: "power1.out" }, "-=0.2");

    });

    return () => ctx.revert();
  }, []);

  // ── Gold shine animation on button — slow ──
  useEffect(() => {
    const el = btnRef.current;
    if (!el) return;
    gsap.to(el, {
      backgroundPosition: "200% center",
      duration: 5,
      ease: "none",
      repeat: -1,
      modifiers: {
        backgroundPosition: (val) => val,
      },
    });
  }, []);

  // // ── Play the current slide's video (if any) and pause the rest ──
  // useEffect(() => {
  //   Object.entries(videoRefs.current).forEach(([idx, el]) => {
  //     if (!el) return;

  //     // Update mute state
  //     el.muted = isMuted;

  //     if (Number(idx) === current) {
  //       el.currentTime = 0;
  //       const playPromise = el.play();

  //       if (playPromise !== undefined) {
  //         playPromise.catch(() => {});
  //       }
  //     } else {
  //       el.pause();
  //     }
  //   });
  // }, [current, isMuted]);

  // ── Slide-change content animation ──
  const animateContentIn = useCallback(() => {
    const targets = [
      eyebrowRef.current,
      headlineRef.current,
      subRef.current,
      ctaRef.current,
      statCardRef.current,
      tagsCardRef.current,
    ].filter(Boolean);

    if (targets.length === 0) return;

    gsap.fromTo(
      targets,
      { opacity: 0, y: 18 },
      { opacity: 1, y: 0, duration: 0.5, ease: "power2.out", stagger: 0.06 }
    );
  }, []);

  const goTo = useCallback((idx) => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    const targets = [
      eyebrowRef.current,
      headlineRef.current,
      subRef.current,
      ctaRef.current,
      statCardRef.current,
      tagsCardRef.current,
    ].filter(Boolean);

    if (targets.length === 0) {
      setCurrent(idx);
      setIsTransitioning(false);
      return;
    }

    gsap.to(targets, {
      opacity: 0,
      y: -12,
      duration: 0.3,
      ease: "power2.in",
      onComplete: () => {
        setCurrent(idx);
        setIsTransitioning(false);
        setTimeout(() => animateContentIn(), 0);
      },
    });
  }, [isTransitioning, animateContentIn]);

  const next = useCallback(
    () => goTo((current + 1) % visibleSlides.length),
    [current, goTo, visibleSlides.length]
  );
  const prev = useCallback(
    () => goTo((current - 1 + visibleSlides.length) % visibleSlides.length),
    [current, goTo, visibleSlides.length]
  );

  // Auto-advance — same 5.5s interval for every slide.
  // On mobile, the video slide advances itself via onEnded, so skip the timer there.
  useEffect(() => {
    const currentSlide = visibleSlides[current];
    if (!currentSlide) return;

    // if (currentSlide.type === "video" && isMobile) return;

    const timer = setTimeout(() => {
      next();
    }, 5500);

    return () => clearTimeout(timer);
  }, [current, next, isMobile, visibleSlides]);

  const s = visibleSlides[current];
  if (!s) return null;

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;700;900&family=Raleway:wght@300;400;500&display=swap"
        rel="stylesheet"
      />
      <style>{`
        .font-cinzel  { font-family: 'Cinzel', serif; }
        .font-raleway { font-family: 'Raleway', sans-serif; }

        .gold-text {
          background: linear-gradient(135deg, #c9943a 0%, #f5d27a 42%, #ffe59a 58%, #c9943a 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .btn-gold {
          background: linear-gradient(105deg,#7d5a0a 0%,#c9943a 20%,#f5d27a 38%,#ffe9a0 50%,#f5d27a 62%,#c9943a 80%,#7d5a0a 100%);
          background-size: 300% auto;
          background-position: 0% center;
          color: #1a0f00;
          font-weight: 700;
          cursor: pointer;
          border: none;
          transition: filter 0.3s ease;
        }
        .btn-gold:hover { filter: brightness(1.1); }

        .glass {
          background: rgba(8, 5, 0, 0.75);
          border: 1px solid rgba(201,168,76,0.2);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
        }

        .stat-gradient {
          background: linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .nav-btn {
          border: 1px solid rgba(255,255,255,0.18);
          color: rgba(255,255,255,0.45);
          transition: border-color 0.2s, color 0.2s;
        }
        .nav-btn:hover { border-color: #c9a84c; color: #c9a84c; }

        /* ── Trust badge: responsive ── */
        .trust-badge {
          position: absolute;
          top: 90px;
          right: 16px;
          left: 16px;
          z-index: 20;
          align-items: center;
          justify-content: center;
          gap: 16px;
          padding: 10px 14px;
          background: rgba(0,0,0,0.55);
          border-radius: 12px;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border: 1px solid rgba(201,168,76,0.15);
          flex-wrap: wrap;
        }

        @media (min-width: 768px) {
          .trust-badge {
            top: 135px;
            right: 32px;
            left: auto;
            gap: 30px;
            padding: 12px 20px;
            width: fit-content;
            flex-wrap: nowrap;
          }
        }
      `}</style>

      <section className="font-cormorant italic relative w-full h-[100svh] min-h-[560px] md:min-h-150 overflow-hidden bg-[#080600] flex flex-col">

        {/* ── BG SLIDES (image or video) ── */}
        {visibleSlides.map((sl, i) => (
          <div
            key={i}
            className="absolute inset-0 transition-opacity duration-500"
            style={{ opacity: i === current ? 1 : 0 }}
          >
            <img
  src={isMobile ? sl.imageMobile : sl.image}
  alt=""
  className="w-full h-full object-cover object-center"
/>
          </div>
        ))}

        {/* {s.type === "video" && isMobile && (
          <button
            onClick={() => setIsMuted(!isMuted)}
            className="absolute bottom-6 right-6 z-30 w-11 h-11 rounded-full bg-black/50 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-black/70 transition"
          >
            {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </button>
        )} */}

        {/* ── Mobile only: dark fade at the bottom so the text stays readable
               below the faces ── */}
        <div className="absolute inset-0 z-[5] md:hidden pointer-events-none bg-gradient-to-t from-black/85 via-black/25 to-transparent" />

        {/* ── CONTENT ── */}
        <div className="relative z-10 flex-1 w-full max-w-7xl mx-auto px-5 sm:px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-end md:justify-between pt-36 sm:pt-40 md:pt-32.5 pb-5 md:pb-17.5 gap-8">

          {/* ═══ LEFT ═══ */}
          {/* On mobile the block fills the height and sits at the bottom
              (justify-end); from md up it is a normal block, as before. */}
          <div className="flex-1 w-full md:max-w-125 mt-0 md:mt-20 flex flex-col justify-end md:block">

            {/* Eyebrow */}
            <div ref={eyebrowRef} className="flex items-center gap-2.5 mb-4 md:mb-4.5">
              <div className="flex">
                {["#c9a84c", "#a07820", "#e0c060", "#b89030"].map((c, i) => (
                  <div
                    key={i}
                    className="w-5.5 h-5.5 md:w-6.5 md:h-6.5 rounded-full border-2 border-[#080600] shrink-0"
                    style={{
                      background: c,
                      marginLeft: i === 0 ? 0 : "-7px"
                    }}
                  />
                ))}
              </div>
              <span className="font-raleway text-[11px] md:text-[13px] text-white/50 font-light">
                {s.eyebrow}
              </span>
            </div>

            {/* Headline */}
            <div ref={headlineRef}>
              <h1
                className="font-cinzel font-black leading-[1.15] md:leading-[1.1] tracking-[0.01em] mb-3 md:mb-4 whitespace-pre-line gold-text"
                style={{ fontSize: "clamp(1.3rem, 4.5vw, 2.5rem)" }}
              >
                {s.headline}
              </h1>
              <p
                className="font-raleway font-light text-[13px] md:text-[14px] text-white/50 leading-[1.6] md:leading-[1.7] max-w-full md:max-w-90 mb-5 md:mb-7"
                ref={subRef}
              >
                {s.sub}
              </p>
            </div>

            {/* CTA */}
            <div ref={ctaRef} className="mb-6 md:mb-9">
              <a
                ref={btnRef}
                href={HT.inPath}
                className="btn-gold inline-flex items-center gap-3 px-5.5 md:px-6.5 py-2.5 md:py-3 rounded-full text-[11px] md:text-[12px] tracking-widest font-cinzel no-underline"
              >
                Explore Treatments
                <span className="w-6 h-6 md:w-6.5 md:h-6.5 rounded-full bg-black/20 flex items-center justify-center">
                  <svg width="11" height="11" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </a>
            </div>

            {/* Slider controls */}
            <div className="flex items-center gap-2 md:gap-2.5 flex-wrap">
              <button
                onClick={prev}
                className="nav-btn w-8 h-8 md:w-9 md:h-9 rounded-full bg-transparent flex items-center justify-center cursor-pointer"
              >
                <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {visibleSlides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className="border-none cursor-pointer p-0 transition-all duration-300 rounded-full"
                  style={{
                    height: "6px",
                    width: i === current ? "24px" : "6px",
                    background: i === current ? "#c9a84c" : "rgba(255,255,255,0.2)",
                  }}
                />
              ))}

              <button
                onClick={next}
                className="nav-btn w-8 h-8 md:w-9 md:h-9 rounded-full bg-transparent flex items-center justify-center cursor-pointer"
              >
                <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <span className="text-white/20 text-[10px] md:text-[11px] font-cinzel tracking-widest">
                {String(current + 1).padStart(2, "0")} / {String(visibleSlides.length).padStart(2, "0")}
              </span>
            </div>

          </div>
          {/* end left */}

        </div>
        {/* end content */}

        {/* ═══ Trust Badge — hidden on mobile, shown from md up ═══ */}
        <div className="trust-badge hidden md:flex">
          {/* ── Google ── */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{
              width: "34px", height: "34px",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <span style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "26px", color: "#c9a84c", lineHeight: 1 }}>G</span>
            </div>

            <div>
              <p style={{ fontSize: "7px", color: "rgba(201,168,76,0.6)", letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 3px 0", fontFamily: "'Cinzel', serif" }}>
                Google Review Rating
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "3px" }}>
                <span style={{ color: "#f5d27a", fontWeight: 700, fontSize: "16px", fontFamily: "'Cinzel', serif", lineHeight: 1 }}>4.8</span>
                <span style={{ color: "#c9a84c", fontSize: "10px", letterSpacing: "1px" }}>★★★★★</span>
              </div>
              <p style={{ margin: 0, fontSize: "7px", color: "rgba(201,168,76,0.35)", fontFamily: "'Raleway', sans-serif" }}>
                Rating 4.9 from 1.2K+ reviews
              </p>
            </div>
          </div>

          {/* ── Divider ── */}
          <div style={{ width: "1px", height: "28px", background: "rgba(201,168,76,0.2)", flexShrink: 0 }} />

          {/* ── Trustindex ── */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{
              width: "34px", height: "34px",
              background: "rgba(201,168,76,0.15)",
              borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <span style={{ color: "#c9a84c", fontSize: "26px", lineHeight: 1, paddingBottom: "4px" }}>★</span>
            </div>

            <div>
              <p style={{ fontSize: "7px", color: "rgba(201,168,76,0.6)", letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 3px 0", fontFamily: "'Cinzel', serif" }}>
                Trustindex Review Rating
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "3px" }}>
                <span style={{ color: "#f5d27a", fontWeight: 700, fontSize: "16px", fontFamily: "'Cinzel', serif", lineHeight: 1 }}>4.6</span>
                <span style={{ fontSize: "10px", letterSpacing: "1px" }}>
                  <span style={{ color: "#c9a84c" }}>★★★★</span>
                  <span style={{ color: "rgba(201,168,76,0.2)" }}>★</span>
                </span>
              </div>
              <p style={{ margin: 0, fontSize: "7px", color: "rgba(201,168,76,0.35)", fontFamily: "'Raleway', sans-serif" }}>
                Rating 4.9 from 900+ reviews
              </p>
            </div>
          </div>
        </div>
        {/* end trust badge */}

        {/* Scroll hint — hidden on mobile, no room */}
        <div
          ref={scrollHintRef}
          className="hidden md:flex absolute bottom-5 left-1/2 -translate-x-1/2 z-10 flex-col items-center gap-1.5 opacity-0"
        >
          <span className="text-white text-[8px] tracking-[0.3em] font-cinzel">SCROLL</span>
          <div className="w-px h-5.5 bg-white/70" />
        </div>

      </section>
    </>
  );
}